// Pure logic shared by TSP Blogs and Success Stories: which categories
// belong to which screen, validation, reading time, excerpts, and the API
// <-> UI shape adapters. No React/I/O so it is unit-tested.

// The ten categories from the requirements (tsp_stories.category ENUM on the
// backend). `kind` decides which screen a category belongs to — an app-level
// grouping only; the backend enum is the same ten categories either way (see
// backend modules/tsp/stories/categoryKey.js, whose toKey/toCategory this
// list's `key`s are the exact reverse of). `tone` maps to a badge tone.
export const storyCategories = [
  { key: "personal-experiences", label: "Personal Experiences", kind: "success", tone: "primary" },
  { key: "inspirational-incidents", label: "Inspirational Incidents", kind: "success", tone: "amber" },
  { key: "miracles-special-experiences", label: "Miracles & Special Experiences", kind: "success", tone: "purple" },
  { key: "book-distribution", label: "Book Distribution", kind: "blog", tone: "green" },
  { key: "harinaam-sankirtan", label: "Harinaam Sankirtan", kind: "blog", tone: "primary" },
  { key: "travel-experiences", label: "Travel Experiences", kind: "blog", tone: "blue" },
  { key: "challenges-faced", label: "Challenges Faced", kind: "blog", tone: "amber" },
  { key: "lessons-learned", label: "Lessons Learned", kind: "blog", tone: "purple" },
  { key: "safety-precautions", label: "Safety & Precautions", kind: "blog", tone: "primary" },
  { key: "best-practices", label: "Best Practices", kind: "blog", tone: "green" },
];

export const STORY_KINDS = {
  success: {
    label: "Success Stories",
    crumb: "Success Stories",
    write: "Share Your Story",
    empty: "No success stories yet",
    emptyHint: "Inspiring incidents and experiences from the road will appear here.",
    searchPlaceholder: "Search stories by title, author or text…",
  },
  blog: {
    label: "Blogs",
    crumb: "Blogs",
    write: "Write a Blog",
    empty: "No blogs yet",
    emptyHint: "Tips, lessons and travel notes from the parties will appear here.",
    searchPlaceholder: "Search blogs by title, author or text…",
  },
};

export const STORY_SORTS = [
  { value: "latest", label: "Latest" },
  { value: "popular", label: "Most popular" },
];

export const STORY_PAGE_SIZE = 6;
export const STORY_TITLE_MIN = 5;
export const STORY_TITLE_MAX = 120;
export const STORY_CONTENT_MIN = 50;
export const STORY_CONTENT_MAX = 5000;
export const COMMENT_MAX = 500;
export const STORY_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
export const STORY_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

export function categoriesOf(kind) {
  return storyCategories.filter((category) => category.kind === kind);
}

export function findCategory(key) {
  return storyCategories.find((category) => category.key === key);
}

// Top-to-bottom on screen — used to focus the first invalid field.
export const STORY_FIELD_ORDER = ["title", "category", "content", "image"];

export function validateStory({ title = "", category = "", content = "" }, kind, image = null) {
  const errors = {};
  const cleanTitle = title.trim();
  const cleanContent = content.trim();

  if (!cleanTitle) errors.title = "Enter a title.";
  else if (cleanTitle.length < STORY_TITLE_MIN) errors.title = `Title must be at least ${STORY_TITLE_MIN} characters.`;
  else if (cleanTitle.length > STORY_TITLE_MAX) errors.title = `Title must be ${STORY_TITLE_MAX} characters or fewer.`;

  if (!category) errors.category = "Choose a category.";
  else if (!categoriesOf(kind).some((entry) => entry.key === category)) errors.category = "Choose a category from the list.";

  if (!cleanContent) errors.content = "Write your story.";
  else if (cleanContent.length < STORY_CONTENT_MIN) errors.content = `Write at least ${STORY_CONTENT_MIN} characters (${cleanContent.length} so far).`;
  else if (cleanContent.length > STORY_CONTENT_MAX) errors.content = `Keep it under ${STORY_CONTENT_MAX} characters.`;

  if (image) {
    if (!STORY_IMAGE_TYPES.includes(image.type)) errors.image = "Use a JPG, PNG or WebP image.";
    else if (image.size > STORY_IMAGE_MAX_BYTES) errors.image = "Image must be 5 MB or smaller.";
  }
  return errors;
}

export function validateComment(text = "") {
  const clean = text.trim();
  if (!clean) return "Write a comment first.";
  if (clean.length > COMMENT_MAX) return `Keep comments under ${COMMENT_MAX} characters.`;
  return "";
}

// ~200 words a minute, never less than one minute.
export function readMinutes(content = "") {
  const words = content.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

export function paragraphsOf(content = "") {
  return content
    .split(/\n{2,}/)
    .map((part) => part.trim())
    .filter(Boolean);
}

export function excerptOf(content = "", max = 150) {
  const flat = content.replace(/\s+/g, " ").trim();
  if (flat.length <= max) return flat;
  const cut = flat.slice(0, max);
  return `${cut.slice(0, Math.max(cut.lastIndexOf(" "), 1)).trimEnd()}…`;
}

// ---- API <-> UI shape --------------------------------------------------------
// The server owns search/category/sort/pagination and every capability
// (story.capabilities.edit/delete) — these adapters only rename fields so
// the existing StoryCard/StoryDetailModal/StoryFormModal keep working.
// There is no cover-image upload yet (see project memory: tsp_media has a
// story_id column, but Phase 4's upload endpoint doesn't accept one) —
// `imageUrl` is always "" until that lands.

export function adaptComment(comment) {
  return { id: comment.id, author: comment.author?.name || "Unknown", createdAt: comment.created_at.slice(0, 10), text: comment.body, capabilities: comment.capabilities };
}

export function adaptStory(story) {
  return {
    id: story.id,
    category: story.category,
    title: story.title,
    author: story.author?.name || "Unknown",
    createdAt: story.created_at.slice(0, 10),
    likes: story.like_count,
    likedByMe: story.liked_by_me,
    content: story.body,
    comments: (story.comments || []).map(adaptComment),
    commentCount: story.comment_count ?? (story.comments || []).length,
    imageUrl: "",
    tone: story.id % 4,
    publicationStatus: story.publication_status,
    capabilities: story.capabilities,
  };
}

// UI filter state -> GET /api/tsp/stories query params. `category` (a
// single key) filters within this screen's own categories; with none
// chosen, every category belonging to this `kind` is sent explicitly (the
// server has no notion of "kind" — see categoryKey.js on the backend) so a
// Blogs reader is never shown a Success Story or vice versa, and pagination
// totals are already scoped correctly rather than over-fetched and filtered
// in React.
export function storyListParams({ search, category, sort, page, limit = STORY_PAGE_SIZE }, kind) {
  const params = { sort, page, limit, category: category || categoriesOf(kind).map((entry) => entry.key).join(",") };
  if (typeof search === "string" && search.trim()) params.search = search.trim();
  return params;
}
